import { Fragment } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { PieceDrawing } from "./PieceDrawing";
import { edgesText, machiningText, pieceLabel } from "../lib/pieces";

const Label = ({ item, piece, project, logo }) => (
  <div data-testid={`label-card-${item.num}`} className="bg-white text-black rounded-sm shadow-lg aspect-[10/7] flex flex-col border-2 border-black overflow-hidden">
    <div className="flex items-center justify-between px-2 py-1 border-b-2 border-black min-h-[3.5rem]">
      {logo ? <img src="/logo-la.png" alt="LA Marcenaria" className="h-14 w-auto" data-testid={`label-logo-${item.num}`} /> : <span />}
      <span className="font-mono font-bold text-xl">{pieceLabel(item.num)}</span>
    </div>
    <div className="flex-1 flex gap-2 p-2 min-h-0">
      <div className="flex-1 min-w-0 text-[11px] leading-tight space-y-0.5">
        <p className="font-bold text-[13px] truncate">{piece.name}</p>
        <p className="font-mono font-bold text-[13px]">{piece.length} x {piece.width} x {piece.thickness}mm</p>
        <p data-testid={`label-client-${item.num}`} className="truncate font-bold">Cliente: {project.client || "-"}</p>
        <p className="truncate font-bold">Ambiente: {project.name || "-"}</p>
        <p className="truncate">MDF: {piece.mdf_color || "-"}</p>
        <p className="truncate">Fita: {piece.edge_color || "-"}</p>
        <p>Fita em: <b>{edgesText(piece)}</b></p>
        <p>Chapa {item.sheet} · Peça {item.copy}/{piece.qty}</p>
      </div>
      <div className="w-[38%] shrink-0"><PieceDrawing piece={piece} mono className="w-full h-full" testId={`label-drawing-${item.num}`} /></div>
    </div>
    {machiningText(piece) && <p className="px-2 pb-1 text-[9px] font-bold truncate">{machiningText(piece)}</p>}
  </div>
);

const SheetSeparator = ({ n, result, project, logo }) => {
  const g = result.groups?.find((x) => x.sheets.some((s) => s.number === n));
  const s = g?.sheets.find((x) => x.number === n);
  const total = result.groups?.reduce((a, x) => a + x.sheets.length, 0);
  return (
    <div data-testid={`label-sheet-separator-${n}`} className="bg-white text-black rounded-sm shadow-lg aspect-[10/7] flex flex-col border-[3px] border-black overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1 border-b-2 border-black min-h-[3.5rem]">
        {logo ? <img src="/logo-la.png" alt="LA Marcenaria" className="h-14 w-auto" /> : <span />}
        <span className="text-xs font-bold tracking-widest">ABERTURA</span>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
        <p className="text-4xl font-black">CHAPA {n}</p>
        <p className="text-sm font-bold">de {total} · MDF {g?.thickness}mm {g?.mdf_color}</p>
        <p className="text-xs">{s?.placed.length} peça(s) nesta chapa</p>
        <p className="text-xs truncate max-w-full">Cliente: {project.client || "-"} · Ambiente: {project.name || "-"}</p>
      </div>
    </div>
  );
};

export const LabelsView = ({ result, pieces, project, loading, error, onDownload, downloading, canDownload, logo, onLogo }) => {
  if (loading) return <div className="flex items-center gap-2 text-[#9CA3AF] p-10"><Loader2 className="w-5 h-5 animate-spin" /> Gerando etiquetas…</div>;
  if (error) return <div data-testid="labels-error" className="rounded-lg border border-red-900 bg-red-950/40 p-4 text-red-300">{error}</div>;
  if (!result) return null;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#9CA3AF]"><b data-testid="labels-count" className="text-white">{result.items.length}</b> etiquetas + {result.groups?.reduce((a, x) => a + x.sheets.length, 0)} de abertura de chapa · formato térmico 100 x 70 mm (uma por página)</p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 rounded-md border border-[#2D333F] px-3 py-2 text-sm cursor-pointer">
            <Switch className="data-[state=checked]:bg-[#E67E22]" data-testid="labels-logo-switch" checked={logo} onCheckedChange={onLogo} />
            Logo da empresa na etiqueta
          </label>
          {canDownload && (
          <Button data-testid="download-labels-btn" onClick={onDownload} disabled={downloading} className="bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
            {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Baixar etiquetas (PDF)
          </Button>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
        {[...result.items].sort((a, b) => (a.sheet || 1e6) - (b.sheet || 1e6) || a.num - b.num).map((it, i, arr) => pieces[it.idx] && (
          <Fragment key={it.num}>
            {it.sheet && it.sheet !== arr[i - 1]?.sheet && <SheetSeparator n={it.sheet} result={result} project={project} logo={logo} />}
            <Label item={it} piece={pieces[it.idx]} project={project} logo={logo} />
          </Fragment>
        ))}
      </div>
    </div>
  );
};
